import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Building2, Check, ChevronsUpDown, Plus, Sparkles, Lock, PartyPopper, XCircle, X, Save } from "lucide-react";
import IconAction from "@/components/app/IconAction";
import api from "@/lib_app/api";
import { toast } from "sonner";
import { RATE_META, fmtIDR } from "@/lib_app/api";
import NumberInput from "@/components/app/NumberInput";
import DateInput from "@/components/app/DateInput";
import { useAuth } from "@/context/AuthContext";
import DealModal from "@/components/app/DealModal";
import Avatar from "@/components/app/Avatar";

const CAT = {
  pemerintah: "Pemerintah",
  bumn: "BUMN",
  swasta: "Swasta",
  partner: "Partner",
};

const empty = {
  customer: "", service: "", rate: 2, prospect_value: 0, realization: 0,
  pic_id: "",
  pic_name: "", pic_position: "", pic_contact: "", pic_phone: "", pic_email: "",
  last_follow_up: null, remark: "",
  pipeline_month: "",
  client_id: null,
  new_client_category: "swasta",
  assigned_sales_id: "",
  lead_source: "",
  lead_source_detail: "",
};

export const LEAD_SOURCE_META = {
  partner: { label: "Partner / Reseller", tint: "bg-[#E4F9EC] text-[#3EB876]" },
  tender: { label: "Tender", tint: "bg-[#FFEED8] text-[#7A5B00]" },
  event: { label: "Event / Pameran", tint: "bg-[#FDE2CF] text-[#8C4A17]" },
  existing_client: { label: "Klien Eksisting", tint: "bg-[#E6E2F4] text-[#4A3B77]" },
  social_media: { label: "Social Media", tint: "bg-[#F8D6E6] text-[#8C3A5B]" },
  website: { label: "Website", tint: "bg-[#E5EEFA] text-[#3B4A63]" },
  cold_call: { label: "Cold Call / Email", tint: "bg-[#D0F1D5] text-[#25603B]" },
  other: { label: "Lainnya", tint: "bg-[#F4F5F7] text-[#626061]" },
};

const currentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function ProspectFormModal({ open, onOpenChange, initial, onSaved, onRequestDeal }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [mode, setMode] = useState("existing"); // "existing" | "new"
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picPickerOpen, setPicPickerOpen] = useState(false);
  const [picMode, setPicMode] = useState("select"); // "select" | "new" — only relevant when mode=existing
  const [dealForProspect, setDealForProspect] = useState(null);
  const { user } = useAuth();
  const isOwnerUser = user?.role === "owner";
  // Read-only: user is NOT owner AND NOT prospect owner (only relevant when editing)
  const readOnly = !!initial?.id && !!user && user.role !== "owner" && initial.owner_id && initial.owner_id !== user.user_id;
  const isEditingActive = !!initial?.id && (initial.status || "active") === "active" && !readOnly;

  const salesOptions = users.filter((u) => u.role !== "owner" && u.role !== "finance" && u.is_active !== false);

  const markLost = async () => {
    if (!window.confirm(`Tandai "${initial.customer}" sebagai Lost?`)) return;
    try {
      await api.post(`/prospects/${initial.id}/lost`, {});
      toast.success("Ditandai Lost");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    }
  };

  useEffect(() => {
    if (!open) return;
    // Load client + users list once modal opens
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    if (isOwnerUser) api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
    if (initial) {
      // Fallback: if pipeline_month is empty on an existing prospect, prefill from creation month
      const fallbackMonth = initial.pipeline_month || (initial.created_at || "").slice(0, 7);
      // Legacy migration: if pic_phone/pic_email empty but pic_contact has value, split it
      let picPhone = initial.pic_phone || "";
      let picEmail = initial.pic_email || "";
      if (!picPhone && !picEmail && initial.pic_contact) {
        if (initial.pic_contact.includes("@")) picEmail = initial.pic_contact;
        else picPhone = initial.pic_contact;
      }
      setForm({ ...empty, ...initial, pipeline_month: fallbackMonth, pic_phone: picPhone, pic_email: picEmail, assigned_sales_id: initial.owner_id || "" });
      setMode(initial.client_id ? "existing" : "new");
      setPicMode("select");
    } else {
      // New prospek: auto-fill current month + today for last follow-up; owner picks sales explicitly
      const today = new Date().toISOString().slice(0, 10);
      setForm({ ...empty, pipeline_month: currentMonthKey(), last_follow_up: today, assigned_sales_id: isOwnerUser ? "" : (user?.user_id || "") });
      setMode("existing");
      setPicMode("select");
    }
  }, [open, initial, isOwnerUser, user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selectedClient = clients.find((c) => c.id === form.client_id);
  const clientPics = selectedClient?.pics || [];

  // Determine if the current form.pic_name matches an existing PIC of the selected client
  const currentPicMatch = clientPics.find(
    (p) => (p.name || "").trim().toLowerCase() === (form.pic_name || "").trim().toLowerCase()
  );

  const chooseClient = (c) => {
    const defaultPic = (c.pics || []).find((p) => p.is_default) || (c.pics || [])[0];
    setForm((f) => ({
      ...f,
      client_id: c.id,
      customer: c.name,
      pic_id: defaultPic?.id || "",
      pic_name: defaultPic?.name || "",
      pic_position: defaultPic?.position || "",
      pic_phone: defaultPic?.phone || "",
      pic_email: defaultPic?.email || "",
      pic_contact: "",
    }));
    setPicMode((c.pics || []).length > 0 ? "select" : "new");
    setPickerOpen(false);
  };

  const choosePic = (p) => {
    setForm((f) => ({
      ...f,
      pic_id: p.id || "",
      pic_name: p.name || "",
      pic_position: p.position || "",
      pic_phone: p.phone || "",
      pic_email: p.email || "",
    }));
    setPicPickerOpen(false);
    setPicMode("select");
  };

  const startNewPic = () => {
    setForm((f) => ({ ...f, pic_id: "", pic_name: "", pic_position: "", pic_phone: "", pic_email: "" }));
    setPicMode("new");
    setPicPickerOpen(false);
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    if (newMode === "new") {
      setForm((f) => ({ ...f, client_id: null, customer: initial?.client_id ? "" : f.customer }));
      setPicMode("new");
    } else {
      setForm((f) => ({ ...f, client_id: null, customer: "" }));
      setPicMode("select");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    if (mode === "existing" && !form.client_id) {
      toast.error("Silakan pilih client");
      return;
    }
    if (mode === "new" && !form.customer.trim()) {
      toast.error("Nama customer wajib diisi");
      return;
    }
    if (isOwnerUser && !form.assigned_sales_id) {
      toast.error("Silakan pilih sales untuk prospek ini");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        customer: form.customer,
        service: form.service,
        rate: Number(form.rate),
        prospect_value: Number(form.prospect_value) || 0,
        realization: Number(form.realization) || 0,
        pic_id: form.pic_id || "",
        pic_name: form.pic_name || "",
        pic_position: form.pic_position || "",
        pic_contact: form.pic_contact || "",
        pic_phone: form.pic_phone || "",
        pic_email: form.pic_email || "",
        last_follow_up: form.last_follow_up || null,
        remark: form.remark || "",
        pipeline_month: form.pipeline_month || null,
        client_id: mode === "existing" ? form.client_id : null,
        new_client_category: mode === "new" ? form.new_client_category : null,
        assigned_sales_id: isOwnerUser ? (form.assigned_sales_id || null) : null,
        lead_source: form.lead_source || null,
        lead_source_detail: form.lead_source_detail || "",
      };
      if (initial?.id) {
        await api.patch(`/prospects/${initial.id}`, payload);
        toast.success("Prospek diperbarui");
      } else {
        await api.post("/prospects", payload);
        toast.success("Prospek ditambahkan");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl rounded-3xl max-h-[92vh] overflow-y-auto border-0 p-0"
        data-testid="prospect-form-modal"
        style={{
          background: "linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(232,240,254,0.92) 100%)",
          boxShadow: "0 32px 80px -20px rgba(59,74,99,0.30)",
        }}
      >
        <DialogTitle className="sr-only">{initial?.id ? "Edit Prospek" : "Tambah Prospek"}</DialogTitle>
        <div className="p-6 space-y-5">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#6B8FE2] font-bold">
            {initial?.id ? (readOnly ? "Detail Prospek · Baca Saja" : "Edit Prospek") : "Tambah Prospek Baru"}
          </p>

          {/* HERO PREVIEW — live from form */}
          {(() => {
            const rateNum = Number(form.rate) || 2;
            const rMeta = rateNum === 1
              ? { label: "Hot", chip: "bg-[#FDE7E9] text-[#CE6968] border-[#FFB3BF]", bg: "linear-gradient(135deg, rgba(255,193,203,0.55), rgba(255,222,228,0.35))", border: "rgba(255,140,155,0.45)" }
              : rateNum === 3
                ? { label: "Cold", chip: "bg-[#E5EEFA] text-[#3B4A63] border-[#B0C7EA]", bg: "linear-gradient(135deg, rgba(190,210,240,0.55), rgba(232,240,254,0.35))", border: "rgba(150,175,220,0.45)" }
                : { label: "Warm", chip: "bg-[#FFDB52] text-[#7A5B00] border-[#F0D28F]", bg: "linear-gradient(135deg, rgba(255,232,178,0.65), rgba(255,244,214,0.35))", border: "rgba(240,200,110,0.45)" };
            const catLabels = { pemerintah: "Pemerintah", bumn: "BUMN", swasta: "Swasta", partner: "Partner" };
            const cat = mode === "existing"
              ? (clients.find((c) => c.id === form.client_id)?.category)
              : form.new_client_category;
            const src = form.lead_source ? LEAD_SOURCE_META[form.lead_source] : null;
            return (
              <div className="rounded-2xl p-5 backdrop-blur-xl" style={{ background: rMeta.bg, border: `1px solid ${rMeta.border}` }}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${rMeta.chip}`}>{rMeta.label} · Stage {rateNum}</span>
                      {cat && <span className="inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#E6E2F4] text-[#4A3B77]">{catLabels[cat]}</span>}
                      {src && <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${src.tint}`}>{src.label}</span>}
                    </div>
                    <h2 className="text-xl font-bold text-[#090808] leading-tight truncate">
                      {form.customer || <span className="text-[#A0A3A8]">Nama customer akan tampil di sini…</span>}
                    </h2>
                    <p className="text-sm text-[#626061] mt-0.5 truncate">
                      {form.service || <span className="text-[#A0A3A8]">Deskripsi service…</span>}
                    </p>
                  </div>
                  {form.pipeline_month && (
                    <span className="inline-flex items-center h-7 px-3 rounded-full text-[11px] font-bold uppercase tracking-widest bg-white/70 text-[#3B4A63] border border-white/80 shrink-0 shadow-sm font-num tabular-nums" data-testid="form-hero-pipeline-month">
                      <span className="text-[9px] text-[#A0A3A8] mr-1.5">BULAN</span>
                      {form.pipeline_month}
                    </span>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs">
                  <div>
                    <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Nilai Prospek</div>
                    <div className="text-sm font-bold text-[#090808] font-num tabular-nums">{fmtIDR(form.prospect_value || 0)}</div>
                  </div>
                  {form.realization > 0 && (
                    <>
                      <div className="w-px h-8 bg-white/70" />
                      <div>
                        <div className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Realisasi</div>
                        <div className="text-sm font-bold text-[#57D28A] font-num tabular-nums">{fmtIDR(form.realization)}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}

          {readOnly && (
            <div className="flex items-start gap-2.5 px-3.5 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="prospect-readonly-banner">
              <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold">Mode Baca Saja</div>
                <div>Prospek ini milik sales lain. Kamu bisa lihat detail & tulis catatan, tapi tidak bisa mengedit atau menandai deal/lost.</div>
              </div>
            </div>
          )}

        <form onSubmit={submit} className="space-y-5 pt-2">
          <fieldset disabled={readOnly} className="space-y-5 disabled:opacity-90">
          {/* Client selector block */}
          <div className="rounded-2xl bg-gradient-to-br from-[#F5F3FF] to-[#F4F5F7] p-4 border border-[#DCE0E7]">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-[10px] uppercase tracking-widest text-[#090808] font-bold">Klien</Label>
              <div className="inline-flex bg-white rounded-full p-1 gap-1 shadow-sm">
                <button
                  type="button"
                  data-testid="prospect-client-mode-existing"
                  onClick={() => switchMode("existing")}
                  className={`text-[11px] font-semibold px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                    mode === "existing" ? "bg-[#090808] text-white" : "text-[#626061] hover:bg-[#F5F3FF]"
                  }`}
                >
                  <Building2 className="w-3 h-3" /> Pilih Klien
                </button>
                <button
                  type="button"
                  data-testid="prospect-client-mode-new"
                  onClick={() => switchMode("new")}
                  className={`text-[11px] font-semibold px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                    mode === "new" ? "bg-[#090808] text-white" : "text-[#626061] hover:bg-[#F5F3FF]"
                  }`}
                >
                  <Plus className="w-3 h-3" /> Klien Baru
                </button>
              </div>
            </div>

            {mode === "existing" ? (
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="prospect-client-picker"
                    className="w-full justify-between bg-white h-11 font-normal"
                  >
                    {selectedClient ? (
                      <span className="flex items-center gap-2 min-w-0">
                        {selectedClient.logo ? (
                          <img src={selectedClient.logo} alt="" className="w-6 h-6 rounded-md object-cover" />
                        ) : (
                          <div className="w-6 h-6 rounded-md bg-[#DCE0E7] text-[#090808] flex items-center justify-center">
                            <Building2 className="w-3 h-3" />
                          </div>
                        )}
                        <span className="truncate font-semibold text-[#090808]">{selectedClient.name}</span>
                        <span className="text-[10px] uppercase tracking-widest text-[#A0A3A8] shrink-0">
                          {CAT[selectedClient.category] || selectedClient.category}
                        </span>
                      </span>
                    ) : (
                      <span className="text-[#A0A3A8]">Pilih client dari database...</span>
                    )}
                    <ChevronsUpDown className="w-4 h-4 text-[#A0A3A8] shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="p-0 w-[--radix-popover-trigger-width]"
                  align="start"
                  onWheel={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                >
                  <Command>
                    <CommandInput placeholder="Cari client..." data-testid="prospect-client-search" />
                    <CommandList className="max-h-72">
                      <CommandEmpty>
                        <div className="text-center py-4">
                          <div className="text-sm text-[#A0A3A8]">Klien tidak ditemukan</div>
                          <button
                            type="button"
                            onClick={() => { switchMode("new"); setPickerOpen(false); }}
                            className="text-xs text-[#090808] font-semibold mt-1 hover:underline"
                          >
                            + Buat client baru
                          </button>
                        </div>
                      </CommandEmpty>
                      <CommandGroup>
                        {clients.map((c) => (
                          <CommandItem
                            key={c.id}
                            value={`${c.name} ${c.category}`}
                            onSelect={() => chooseClient(c)}
                            data-testid={`prospect-client-option-${c.id}`}
                            className="gap-2"
                          >
                            {c.logo ? (
                              <img src={c.logo} alt="" className="w-7 h-7 rounded-md object-cover" />
                            ) : (
                              <div className="w-7 h-7 rounded-md bg-[#DCE0E7] text-[#090808] flex items-center justify-center">
                                <Building2 className="w-3.5 h-3.5" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold truncate">{c.name}</div>
                              <div className="text-[10px] text-[#A0A3A8] uppercase tracking-widest">
                                {CAT[c.category] || c.category}
                              </div>
                            </div>
                            {form.client_id === c.id && <Check className="w-4 h-4 text-[#090808]" />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                <Field label="Nama Customer" span={2}>
                  <Input
                    data-testid="form-customer"
                    value={form.customer}
                    onChange={(e) => set("customer", e.target.value)}
                    required
                    placeholder="mis. PT Sinar Abadi"
                  />
                </Field>
                <Field label="Kategori">
                  <Select value={form.new_client_category} onValueChange={(v) => set("new_client_category", v)}>
                    <SelectTrigger data-testid="form-new-client-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(CAT).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="col-span-3 flex items-start gap-2 text-[11px] text-[#A0A3A8] leading-relaxed">
                  <Sparkles className="w-3 h-3 mt-0.5 text-[#090808] shrink-0" />
                  Client baru akan otomatis ditambahkan ke database saat prospek disimpan.
                </div>
              </div>
            )}
          </div>

          {/* Detail Prospek — Client-style container */}
          <div className="rounded-2xl border-2 border-[#B0C7EA] bg-[#F5F9FF]/50 p-5 space-y-4 [&_input:not([type=checkbox]):not([type=radio])]:h-11 [&_input:not([type=checkbox]):not([type=radio])]:rounded-xl [&_input:not([type=checkbox]):not([type=radio])]:bg-white [&_[role=combobox]]:h-11 [&_[role=combobox]]:rounded-xl [&_[role=combobox]]:bg-white">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#3B4A63] font-bold">Detail Prospek</div>

            {/* Row 1: Service (full-width) */}
            <div className="grid grid-cols-1 gap-4">
              <Field label="Service">
                <Input data-testid="form-service" value={form.service} onChange={(e) => set("service", e.target.value)} required placeholder="Nama layanan / paket yang ditawarkan" />
              </Field>
            </div>

            {/* Row 2: Prospect Value | Stage | Sales (each 1/3, Sales owner-only) */}
            <div className={`grid gap-4 ${isOwnerUser ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2"}`}>
              <Field label="Prospect Value (Rp)">
                <NumberInput data-testid="form-value" value={form.prospect_value} onChange={(v) => set("prospect_value", v)} />
              </Field>
              <Field label="Stage">
                <Select value={String(form.rate)} onValueChange={(v) => set("rate", Number(v))}>
                  <SelectTrigger data-testid="form-rate"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3].map((r) => (
                      <SelectItem key={r} value={String(r)}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${RATE_META[r].dot}`} /> {r} · {RATE_META[r].label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {isOwnerUser && (
                <Field label="Sales">
                  <Select value={form.assigned_sales_id || ""} onValueChange={(v) => set("assigned_sales_id", v)}>
                    <SelectTrigger data-testid="form-assigned-sales">
                      <SelectValue placeholder="Pilih sales..." />
                    </SelectTrigger>
                    <SelectContent>
                      {salesOptions.map((s) => (
                        <SelectItem key={s.user_id} value={s.user_id} data-testid={`form-sales-option-${s.user_id}`}>
                          <div className="flex items-center gap-2">
                            <Avatar name={s.name} picture={s.picture} size="sm" className="!w-5 !h-5 !text-[9px] !border-0" />
                            <span className="text-sm font-semibold">{s.nickname || s.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>

            {/* Row 2b: Sumber Prospek | Sumber (detail) */}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Sumber Prospek">
                <Select value={form.lead_source || ""} onValueChange={(v) => set("lead_source", v)}>
                  <SelectTrigger data-testid="form-lead-source">
                    <SelectValue placeholder="Dari mana prospek ini datang?" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(LEAD_SOURCE_META).map(([k, v]) => (
                      <SelectItem key={k} value={k} data-testid={`form-lead-source-${k}`}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={`Detail Sumber${form.lead_source === "referral" ? " *" : ""}`}>
                <Input
                  data-testid="form-lead-source-detail"
                  value={form.lead_source_detail || ""}
                  onChange={(e) => set("lead_source_detail", e.target.value)}
                  placeholder={
                    form.lead_source === "partner" ? "Nama partner / reseller"
                      : form.lead_source === "tender" ? "Portal (LPSE) / lembaga"
                        : form.lead_source === "event" ? "Nama event / pameran"
                          : form.lead_source === "social_media" ? "Platform / akun / kata kunci ads"
                            : form.lead_source === "existing_client" ? "Nama klien lama"
                              : "Info tambahan (opsional)"
                  }
                />
              </Field>
            </div>

            {/* Row 3: Bulan Pipeline | Last Follow-up */}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Bulan Pipeline">
                <DateInput
                  variant="month"
                  data-testid="form-pipeline-month"
                  value={form.pipeline_month || ""}
                  onChange={(e) => set("pipeline_month", e.target.value)}
                  placeholder="YYYY-MM"
                />
              </Field>
              <Field label="Last Follow-up">
                <DateInput
                  data-testid="form-followup"
                  value={form.last_follow_up || ""}
                  onChange={(e) => set("last_follow_up", e.target.value || null)}
                />
              </Field>
            </div>
          </div>

          {/* PIC & Catatan — Client-style container */}
          <div className="rounded-2xl border-2 border-[#B0C7EA] bg-[#F5F9FF]/50 p-5 space-y-4 [&_input:not([type=checkbox]):not([type=radio])]:h-11 [&_input:not([type=checkbox]):not([type=radio])]:rounded-xl [&_input:not([type=checkbox]):not([type=radio])]:bg-white [&_textarea]:rounded-xl [&_textarea]:bg-white [&_[role=combobox]]:h-11 [&_[role=combobox]]:rounded-xl [&_[role=combobox]]:bg-white">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#3B4A63] font-bold">PIC & Catatan</div>

            {/* PIC block */}
            <div className="grid grid-cols-2 gap-4">
              {mode === "existing" && selectedClient && picMode === "select" ? (
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-[#3B4A63]">PIC</Label>
                <div className="flex gap-2">
                  <Popover open={picPickerOpen} onOpenChange={setPicPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        data-testid="form-pic-picker"
                        className="flex-1 justify-between bg-white h-11 font-normal"
                      >
                        {form.pic_name ? (
                          <span className="flex items-center gap-2 min-w-0 text-left">
                            <div className="w-7 h-7 rounded-full bg-[#DCE0E7] text-[#090808] font-bold flex items-center justify-center text-[10px] shrink-0">
                              {(form.pic_name || "").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?"}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-[#090808] truncate leading-tight">{form.pic_name}</div>
                              {form.pic_position && <div className="text-[10px] text-[#A0A3A8] truncate leading-tight">{form.pic_position}</div>}
                            </div>
                          </span>
                        ) : (
                          <span className="text-[#A0A3A8]">Pilih PIC dari client...</span>
                        )}
                        <ChevronsUpDown className="w-4 h-4 text-[#A0A3A8] shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="p-0 w-[--radix-popover-trigger-width]"
                      align="start"
                      onWheel={(e) => e.stopPropagation()}
                      onTouchMove={(e) => e.stopPropagation()}
                    >
                      <Command>
                        <CommandInput placeholder="Cari PIC..." data-testid="form-pic-search" />
                        <CommandList className="max-h-72">
                          <CommandEmpty>
                            <div className="text-center py-4">
                              <div className="text-sm text-[#A0A3A8]">PIC tidak ditemukan</div>
                            </div>
                          </CommandEmpty>
                          <CommandGroup heading={`${clientPics.length} PIC tersimpan`}>
                            {clientPics.map((p) => (
                              <CommandItem
                                key={p.id || p.name}
                                value={`${p.name} ${p.position || ""} ${p.email || ""} ${p.phone || ""}`}
                                onSelect={() => choosePic(p)}
                                data-testid={`form-pic-option-${p.id || p.name}`}
                                className="gap-2"
                              >
                                <div className="w-8 h-8 rounded-full bg-[#DCE0E7] text-[#090808] font-bold text-[10px] flex items-center justify-center shrink-0">
                                  {(p.name || "").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?"}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                                    {p.name}
                                    {p.is_default && <span className="inline-flex items-center h-4 px-1.5 rounded-full bg-[#FFE89A] text-[#7A6633] text-[9px] font-bold">DEFAULT</span>}
                                  </div>
                                  <div className="text-[10px] text-[#A0A3A8] truncate">
                                    {[p.position, p.phone, p.email].filter(Boolean).join(" · ") || "Tanpa detail"}
                                  </div>
                                </div>
                                {currentPicMatch?.id === p.id && <Check className="w-4 h-4 text-[#090808]" />}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                        <div className="border-t border-[#DCE0E7] p-2">
                          <button
                            type="button"
                            onClick={startNewPic}
                            data-testid="form-pic-add-new"
                            className="w-full inline-flex items-center gap-2 h-9 px-3 rounded-lg text-sm font-semibold text-[#090808] hover:bg-[#F5F3FF] transition-colors"
                          >
                            <Plus className="w-4 h-4" /> Tambah PIC baru
                          </button>
                        </div>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <p className="text-[10px] text-[#A0A3A8]">PIC yang dipilih akan digunakan pada prospek ini.</p>
              </div>
            ) : (
              <>
                <div className="col-span-2 flex items-center justify-between">
                  <Label className="text-[10px] uppercase tracking-widest font-bold text-[#3B4A63]">
                    {mode === "existing" ? "PIC Baru" : "PIC"}
                  </Label>
                  {mode === "existing" && selectedClient && clientPics.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setPicMode("select")}
                      data-testid="form-pic-back-to-select"
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#090808] hover:underline"
                    >
                      ← Pilih PIC existing
                    </button>
                  )}
                </div>
                {mode === "existing" && selectedClient && (
                  <div className="col-span-2 flex items-start gap-2 text-[11px] text-[#A0A3A8] leading-relaxed -mt-1">
                    <Sparkles className="w-3 h-3 mt-0.5 text-[#090808] shrink-0" />
                    PIC baru ini akan otomatis ditambahkan ke database client saat disimpan.
                  </div>
                )}
                <Field label="PIC Name">
                  <Input data-testid="form-pic-name" value={form.pic_name} onChange={(e) => set("pic_name", e.target.value)} placeholder="Nama lengkap" />
                </Field>
                <Field label="PIC Position">
                  <Input data-testid="form-pic-position" value={form.pic_position} onChange={(e) => set("pic_position", e.target.value)} placeholder="mis. Manager, Direktur" />
                </Field>
                <Field label="No HP">
                  <Input data-testid="form-pic-phone" value={form.pic_phone} onChange={(e) => set("pic_phone", e.target.value)} placeholder="08xxxxxxxxxx" />
                </Field>
                <Field label="Email">
                  <Input data-testid="form-pic-email" type="email" value={form.pic_email} onChange={(e) => set("pic_email", e.target.value)} placeholder="nama@perusahaan.co.id" />
                </Field>
              </>
            )}
            </div>
            <Field label="Remark">
              <Textarea data-testid="form-remark" value={form.remark} onChange={(e) => set("remark", e.target.value)} rows={3} placeholder="Catatan awal, kebutuhan klien, konteks penting…" />
            </Field>
          </div>
          </fieldset>
          <DialogFooter className="!justify-between gap-2 flex-wrap">
            <div className="flex flex-wrap items-center gap-2">
              {isEditingActive && (
                <>
                  <Button
                    type="button"
                    onClick={() => {
                      if (onRequestDeal) {
                        // Replace flow — close this modal, let parent open Deal modal
                        onRequestDeal(initial);
                        onOpenChange(false);
                      } else {
                        setDealForProspect(initial);
                      }
                    }}
                    data-testid="form-mark-deal-btn"
                    className="gap-1.5 h-10 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-[0_4px_10px_rgba(16,185,129,0.25)]"
                  >
                    <PartyPopper className="w-4 h-4" /> Tandai Deal
                  </Button>
                  <IconAction icon={XCircle} label="Tandai Lost" variant="danger" onClick={markLost} testid="form-mark-lost-btn" />
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <IconAction icon={X} label={readOnly ? "Tutup" : "Batal"} onClick={() => onOpenChange(false)} testid="form-cancel-btn" />
              {!readOnly && (
                <IconAction icon={Save} label={busy ? "Menyimpan..." : "Simpan"} variant="primary" type="submit" disabled={busy} testid="form-save-btn" />
              )}
            </div>
          </DialogFooter>
        </form>
        </div>
      </DialogContent>
      <DealModal
        prospect={dealForProspect}
        onOpenChange={(o) => { if (!o) setDealForProspect(null); }}
        onDone={() => { setDealForProspect(null); onSaved?.(); onOpenChange(false); }}
      />
    </Dialog>
  );
}

function Field({ label, span = 1, children }) {
  return (
    <div className={`space-y-1.5 ${span === 2 ? "col-span-2" : ""}`}>
      <Label className="text-[10px] uppercase tracking-widest font-bold text-[#3B4A63]">{label}</Label>
      {children}
    </div>
  );
}
